"use client";

import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/navigation";
import { getAgents } from "@/lib/web-client";
import type { Agent } from "@/lib/web-client/types";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";
const URB = "var(--font-urbanist), sans-serif";

// ── Avatar palette — brand colors only ──────────────────────────────
const AVATAR_PALETTE: Array<{ color: string; gradFrom: string; gradTo: string }> = [
  { color: "#8635F6", gradFrom: "rgba(134,53,246,0.2)",  gradTo: "rgba(134,187,216,0.2)" },
  { color: "#F6AE2D", gradFrom: "rgba(246,174,45,0.2)",  gradTo: "rgba(242,100,25,0.2)"  },
  { color: "#86BBD8", gradFrom: "rgba(134,187,216,0.2)", gradTo: "rgba(134,53,246,0.2)"  },
  { color: "#F26419", gradFrom: "rgba(242,100,25,0.2)",  gradTo: "rgba(246,174,45,0.2)"  },
];

/** Simple djb2 hash → deterministic palette index */
function hashId(id: string): number {
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = ((h << 5) + h) ^ id.charCodeAt(i);
  return Math.abs(h) % AVATAR_PALETTE.length;
}

/** Format a raw task count number to a display string */
function fmtTaskCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Map a live Agent to display-ready fields */
interface DisplayAgent {
  id: string;
  name: string;
  initials: string;
  initialColor: string;
  gradientFrom: string;
  gradientTo: string;
  rating: number;
  taskCount: string;
  price: string;
  tags: string[];
  description: string;
}

function toDisplayAgent(a: Agent): DisplayAgent {
  const p = AVATAR_PALETTE[hashId(a.id)];
  return {
    id: a.id,
    name: a.name,
    initials: a.avatarInitials,
    initialColor: p.color,
    gradientFrom: p.gradFrom,
    gradientTo: p.gradTo,
    rating: a.rating,
    taskCount: fmtTaskCount(a.tasksCompleted),
    price: `$${a.priceMin}-${a.priceMax}`,
    tags: a.categories,
    description: a.description,
  };
}

interface AgentBrowserModalProps {
  onClose?: () => void;
  onSelect?: (agentId: string) => void;
  /**
   * "modal" (default) — floating overlay used by the chat page.
   * "page"            — flat content block used by /app/marketplace.
   *                     Drops the modal chrome (fixed width, glass
   *                     background, border, shadow, close button) and
   *                     fills the parent scroll container.
   */
  mode?: "modal" | "page";
}

export default function AgentBrowserModal({
  onClose,
  onSelect,
  mode = "modal",
}: AgentBrowserModalProps = {}) {
  const isPage = mode === "page";
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [searchText, setSearchText] = useState<string>("");
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAgents()
      .then((data) => { if (!cancelled) setAgents(data); })
      .catch((err) => { if (!cancelled) setFetchError(String(err)); });
    return () => { cancelled = true; };
  }, []);

  const loading = agents === null && fetchError === null;

  const displayAgents: DisplayAgent[] = useMemo(
    () => (agents ?? []).map(toDisplayAgent),
    [agents],
  );

  // Derive category list from loaded data — dedupe + sort, prepend "All"
  const categories: string[] = useMemo(() => {
    if (!agents) return ["All"];
    const cats = new Set<string>();
    for (const a of agents) for (const c of a.categories) cats.add(c);
    return ["All", ...[...cats].sort()];
  }, [agents]);

  const handleClose = () => {
    if (onClose) onClose();
    else router.push("/app");
  };
  const handleSelectById = (agentId: string) => {
    if (onSelect) onSelect(agentId);
    else router.push(`/app/agent/${agentId}`);
  };

  const filteredAgents = useMemo(() => {
    let list = displayAgents;
    if (selectedCategory !== "All") {
      list = list.filter((a) =>
        a.tags.some((t) => t.toLowerCase() === selectedCategory.toLowerCase()),
      );
    }
    const q = searchText.trim().toLowerCase();
    if (q) list = list.filter((a) => a.name.toLowerCase().includes(q));
    return list;
  }, [displayAgents, selectedCategory, searchText]);

  return (
    <div
      className={isPage ? undefined : "app-anim-scale-in desktop-glass-modal"}
      style={
        isPage
          ? {
              // Page mode: fill the parent scroll container, no chrome.
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }
          : {
              width: 1024,
              background: "#171717",
              border: "1px solid rgba(242,240,237,0.1)",
              borderRadius: 24,
              boxShadow: "0px 0px 50px 0px rgba(0,0,0,0.5)",
              overflow: "clip",
              padding: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }
      }
    >
      {/* Header — sticky at the top of the page scroll container in
          page mode so the title stays pinned during scroll. */}
      <div
        style={{
          width: "100%",
          padding: isPage ? "40px 0 16px" : "24px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          ...(isPage
            ? {
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "#201F1F",
              }
            : {}),
        }}
      >
        <h2
          style={{
            fontFamily: PJK,
            fontWeight: 700,
            fontSize: 24,
            lineHeight: "32px",
            color: "#F2F0ED",
            margin: 0,
          }}
        >
          {isPage ? "Marketplace" : "Browse Agents"}
        </h2>
        {!isPage && <button
          onClick={handleClose}
          aria-label="Close"
          className="app-close-btn"
          style={{
            width: 42.8,
            height: 42.8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <span style={{ display: "flex", transform: "scale(0.95)" }}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                padding: 12,
                borderRadius: 9999,
              }}
            >
              <img
                src="/app-icons/multiply.svg"
                alt=""
                style={{ width: 20, height: 20 }}
              />
            </span>
          </span>
        </button>}
      </div>

      {/* Search + Filter */}
      <div
        style={{
          width: "100%",
          padding: isPage ? "0 0 16px" : "0 32px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {/* Search input */}
        <div
          className="app-search-container"
          style={{
            background: "rgba(242,240,237,0.05)",
            border: "1px solid rgba(242,240,237,0.1)",
            borderRadius: 9999,
            padding: "17px 21px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <img
            src="/app-icons/search.svg"
            alt=""
            style={{ width: 20, height: 20, flexShrink: 0 }}
          />
          <input
            placeholder="Search agents..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              fontFamily: PJK,
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "26px",
              color: "#F2F0ED",
            }}
          />
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              aria-label="Clear search"
              className="app-close-btn"
              style={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
                opacity: 0.6,
              }}
            >
              <img src="/app-icons/multiply.svg" alt="" style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>

        {/* Category pills — derived from live data */}
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            overflowX: "clip",
            paddingBottom: 8,
            width: "100%",
          }}
        >
          {categories.map((label) => {
            const isSelected = label === selectedCategory;
            if (isSelected) {
              return (
                <button
                  key={label}
                  onClick={() => setSelectedCategory(label)}
                  className="app-btn-primary"
                  style={{
                    background: "#8635F6",
                    height: 36,
                    borderRadius: 9999,
                    padding: "0 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: URB,
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: "20px",
                    color: "#F2F0ED",
                    flexShrink: 0,
                  }}
                >
                  {label}
                </button>
              );
            }
            return (
              <button
                key={label}
                onClick={() => setSelectedCategory(label)}
                className="app-cat-pill"
                style={{
                  border: "2px solid #2E2A33",
                  background: "transparent",
                  height: 36,
                  borderRadius: 40,
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontFamily: URB,
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "rgba(242,240,237,0.7)",
                  flexShrink: 0,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Agent Grid */}
      <div style={{ width: "100%", padding: isPage ? "0 0 12px" : "0 32px 12px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 16,
            width: "100%",
          }}
        >
          {loading && (
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="app-loading-pulse"
                  style={{
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    background: "rgba(242,240,237,0.04)",
                    border: "1px solid rgba(242,240,237,0.08)",
                    borderRadius: 24,
                    padding: 21,
                    height: 220,
                  }}
                />
              ))}
            </>
          )}
          {fetchError && (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "60px 0",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 16,
              }}
            >
              <span
                style={{
                  color: "rgba(242,240,237,0.5)",
                  fontFamily: PJK,
                  fontSize: 15,
                  fontWeight: 500,
                }}
              >
                Failed to load agents.
              </span>
              <button
                onClick={() => {
                  setAgents(null);
                  setFetchError(null);
                  getAgents()
                    .then(setAgents)
                    .catch((err) => setFetchError(String(err)));
                }}
                className="app-btn-primary"
                style={{
                  background: "#8635F6",
                  borderRadius: 9999,
                  padding: "10px 24px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: URB,
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "#F2F0ED",
                }}
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !fetchError && filteredAgents.map((agent) => (
            <MarketplaceAgentCard
              key={agent.id}
              agent={agent}
              onSelect={() => handleSelectById(agent.id)}
            />
          ))}
          {!loading && !fetchError && agents !== null && filteredAgents.length === 0 && (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "60px 0",
                textAlign: "center",
                color: "rgba(242,240,237,0.5)",
                fontFamily: PJK,
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {agents.length === 0 ? "No agents available right now." : "No agents found."}
            </div>
          )}
        </div>
      </div>

      {/* Pagination footer */}
      <div
        style={{
          width: "100%",
          background: isPage ? "transparent" : "#171717",
          padding: isPage ? "24px 0" : "24px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontFamily: URB,
            fontWeight: 400,
            fontSize: 14,
            lineHeight: "20px",
            letterSpacing: "0.7px",
            textTransform: "uppercase",
            color: "rgba(242,240,237,0.4)",
          }}
        >
          {loading
            ? "LOADING…"
            : fetchError
            ? "—"
            : `SHOWING 1–${filteredAgents.length} OF ${agents?.length ?? 0} AGENTS`}
        </span>
        <div style={{ display: "flex", gap: 12 }}>
          <PaginationButton direction="left" />
          <PaginationButton direction="right" />
        </div>
      </div>
    </div>
  );
}

function PaginationButton({ direction }: { direction: "left" | "right" }) {
  return (
    <button
      aria-label={direction === "left" ? "Previous page" : "Next page"}
      className="app-pag-btn"
      style={{
        width: 40,
        height: 40,
        border: "1px solid rgba(242,240,237,0.1)",
        borderRadius: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        cursor: "pointer",
        color: "rgba(242,240,237,0.5)",
      }}
    >
      <img
        src={`/app-icons/angle-${direction}.svg`}
        alt=""
        style={{ width: 20, height: 20 }}
      />
    </button>
  );
}

function MarketplaceAgentCard({
  agent,
  onSelect,
}: {
  agent: DisplayAgent;
  onSelect: () => void;
}) {
  return (
    <div
      className="app-card-mkt"
      style={{
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        background: "rgba(242,240,237,0.04)",
        border: "1px solid rgba(242,240,237,0.08)",
        borderRadius: 24,
        padding: 21,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        alignItems: "flex-start",
      }}
    >
      {/* Top row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          {/* Avatar */}
          <div
            className="app-card-mkt-avatar"
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${agent.gradientFrom} 0%, ${agent.gradientTo} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "filter 200ms ease-out",
            }}
          >
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 700,
                fontSize: 20,
                lineHeight: "28px",
                color: agent.initialColor,
              }}
            >
              {agent.initials}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <span
              style={{
                fontFamily: PJK,
                fontWeight: 700,
                fontSize: 18,
                lineHeight: "28px",
                color: "#F2F0ED",
              }}
            >
              {agent.name}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <img
                src="/app-icons/star-mini.svg"
                alt=""
                style={{ width: 11.667, height: 11.083 }}
              />
              <span
                style={{
                  fontFamily: URB,
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: "20px",
                  color: "#F6AE2D",
                }}
              >
                {agent.rating.toFixed(1)}
              </span>
              <span
                style={{
                  fontFamily: URB,
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "16px",
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  color: "rgba(242,240,237,0.3)",
                  paddingLeft: 8,
                }}
              >
                {agent.taskCount} TASKS
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span
            style={{
              fontFamily: URB,
              fontWeight: 700,
              fontSize: 18,
              lineHeight: "28px",
              color: "#F6AE2D",
              textAlign: "right",
            }}
          >
            {agent.price}
          </span>
          <span
            style={{
              fontFamily: URB,
              fontWeight: 600,
              fontSize: 10,
              lineHeight: "15px",
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: "rgba(242,240,237,0.3)",
              textAlign: "right",
            }}
          >
            PER TASK
          </span>
        </div>
      </div>

      {/* Tags */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", width: "100%" }}>
        {agent.tags.map((t) => (
          <div
            key={t}
            style={{
              background: "rgba(134,187,216,0.1)",
              borderRadius: 6,
              padding: "2px 8px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontFamily: URB,
                fontWeight: 700,
                fontSize: 10,
                lineHeight: "15px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#86BBD8",
              }}
            >
              {t}
            </span>
          </div>
        ))}
      </div>

      {/* Description */}
      <p
        style={{
          fontFamily: URB,
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "20px",
          color: "rgba(242,240,237,0.6)",
          margin: 0,
          width: "100%",
        }}
      >
        {agent.description}
      </p>

      {/* Select button */}
      <div style={{ width: "100%", paddingTop: 8 }}>
        <button
          onClick={onSelect}
          className="app-btn-primary"
          style={{
            background: "#8635F6",
            borderRadius: 9999,
            width: "100%",
            paddingTop: 12,
            paddingBottom: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            cursor: "pointer",
            fontFamily: URB,
            fontWeight: 700,
            fontSize: 14,
            lineHeight: "20px",
            color: "#F2F0ED",
            textAlign: "center",
          }}
        >
          Select Agent
        </button>
      </div>
    </div>
  );
}
